// external libs
import { createReducer, createActions } from 'reduxsauce'
import Immutable from 'seamless-immutable'
import localforage from 'localforage'

// config
import { config } from '../../Config/ConfigLoader'

import { checkIfShouldBeDeleted } from '../ContainerFunctions'

export const localForageSuriData = localforage.createInstance({
  name: 'suriData',
})

const savePhoneNumber = (phoneNumber: string) => {
  localForageSuriData.setItem('phoneNumber', phoneNumber)
}

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  copyDataForFieldReferences: ['stepKey', 'fieldReferences', 'quoteId'],
  defaultValuesRequestLoaderOff: ['productName', 'subProductName', 'isBackOffice'],
  defaultValuesRequestSuccess: ['defaultValues'],
  onChangeData: ['data', 'loaded'],
  onSetRequestId: ['previousRequestId'],
  restoreDataRequest: ['linkId', 'productName', 'subProductName'],
  restoreDataRequestSuccess: ['data', 'defaultData', 'linkId'],
  setIsBackOffice: ['isBackOffice'],
  setNumberOfItemsInArrayType: ['key', 'path', 'value'],
  setPayment: ['payment'],
  supplementaryInsuranceRemove: ['key', 'quoteId'],
  supplementaryInsuranceReset: ['quoteId', 'data'],
  supplementaryInsuranceSetValue: ['key', 'value', 'quoteId'],
  supplementaryInsuranceChange: ['supplementaryInsurances', 'quoteId'],
  unsetInitialData: null,
  updateArrayTypes: ['specification'],
  userDataInit: ['specification'],
  userDataRemoveValue: ['key'],
  userDataSetValue: ['key', 'value'],
  userDataSetValueInsuredParties: ['pathArray', 'value'],
  wasCicCompleted: ['firstStepCicCompleted'],
  setInitDataSig: ['initDataSig'],
  setTrackingParams: ['trackingParams', 'reset'],
  setProperties: ['properties'],
  setSessionMinutesValue: ['path', 'value'],
})

export const UserDataTypes = Types
export default Creators

/* ------------- Helpers ------------- */

const getDefaultValueForType = (type) => {
  const values = {
    object: {},
    array: [],
    bool: false,
  }
  if (typeof values[type] !== 'undefined') {
    return values[type]
  }
  return ''
}

export const initDataBySpecification = (specification, data, arrayTypes, prefix = '') => {
  let rData = null
  specification.fields.map((field) => {
    if (field.domain._type === 'object') {
      if (!data[field.name]) {
        rData = initDataBySpecification(field.domain.specification, {}, arrayTypes, `${prefix}${field.name}-`)
        if (field.name !== 'cic') {
          data[field.name] = rData.data
        }
        arrayTypes = rData.arrayTypes
      } else if (field.name === 'cic') {
        if (checkIfShouldBeDeleted(field.name, data[field.name], true)) {
          delete data[field.name]
        }
      } else {
        rData = initDataBySpecification(
          field.domain.specification,
          { ...data[field.name] },
          arrayTypes,
          `${prefix}${field.name}-`,
        )
        data[field.name] = rData.data
        arrayTypes = rData.arrayTypes
      }
    } else if (field.domain._type === 'array') {
      const minLength = field.domain.minLength
      const maxLength = field.domain.maxLength
      if (!arrayTypes[field.name]) {
        arrayTypes[field.name] = {
          length: minLength === 1 ? 2 : minLength,
          minLength,
          maxLength,
          path: prefix,
        }
      }
      if (!data[field.name]) {
        rData = initDataBySpecification(field.domain.specification, {}, arrayTypes, `${prefix}${field.name}-`)
        data[field.name] = new Array(arrayTypes[field.name].length).fill(rData.data)
        arrayTypes = rData.arrayTypes
      }
    } else if (field.domain._type === 'bool') {
      data[field.name] = data && typeof data[field.name] === 'boolean' ? data[field.name] : false
    } else {
      data[field.name] =
        data && typeof data[field.name] !== 'undefined' ? data[field.name] : getDefaultValueForType(field.domain._type)
    }
  })
  return { arrayTypes, data }
}

export const updateArrayTypes = (specification, arrayTypes, prefix = '') => {
  specification.fields.map((field) => {
    if (field.domain._type === 'object') {
      arrayTypes = updateArrayTypes(field.domain.specification, arrayTypes, `${prefix}${field.name}-`)
    } else if (field.domain._type === 'array') {
      const minLength = field.domain.minLength
      const maxLength = field.domain.maxLength
      if (!arrayTypes[field.name]) {
        arrayTypes[field.name] = {
          length: minLength === 1 ? 2 : minLength,
          minLength,
          maxLength,
          path: prefix,
        }
      } else {
        arrayTypes[field.name] = {
          ...arrayTypes[field.name],
          length: arrayTypes[field.name].length === null ? minLength : arrayTypes[field.name].length,
          minLength,
          maxLength,
        }
      }
      arrayTypes = updateArrayTypes(field.domain.specification, arrayTypes, `${prefix}${field.name}-`)
    }
  })
  return arrayTypes
}

/* ------------- Initial State ------------- */

export const INITIAL_STATE = Immutable({
  // eslint-disable-line
  initialData: true,
  initDataSig: false,
  data: config.getConfig('defaultData'),
  loadedData: {},
  defaultValues: {},
  supplementaryInsurances: {},
  arrayTypes: {},
  firstStepCicCompleted: false,
  previousRequestId: '',
  trackingParams: {},
  properties: {},
  restoredLink: '',
  sessionMinutes: {
    recommendedReasonText: '',
    substantialChanges: '',
    substantialImpactChanges: '',
    clientBases: {
      noInformation: false,
      clientParameters: false,
      closedContract: false,
      copyOfDocument: false,
      carPhotoDocument: false,
      technicalCard: false,
    },
  },
})

/* ------------- Reducers ------------- */

const userDataRemoveValueR = (state, { key }) => {
  const data = state.data.asMutable()
  delete data[key]
  return state.set('data', data)
}

const userDataInitR = (state, { specification }) => {
  const { data, arrayTypes } = initDataBySpecification(
    specification,
    state.data.asMutable(),
    state.arrayTypes.asMutable(),
  )
  return state.merge({ data: data, arrayTypes })
}

const supplementaryInsuranceSetValueR = (state, { key, value, quoteId }) =>
  state.setIn(['supplementaryInsurances', quoteId, ...(Array.isArray(key) ? key : [key])], value)

const supplementaryInsuranceChangeR = (state, { supplementaryInsurances, quoteId }) =>
  state.setIn(['supplementaryInsurances', quoteId], supplementaryInsurances)

const supplementaryInsuranceResetR = (state, { quoteId, data }) =>
  state.setIn(['supplementaryInsurances', quoteId], data)

const supplementaryInsuranceRemoveR = (state, { key, quoteId }) => {
  const supplementaryInsurances = state.supplementaryInsurances[quoteId].asMutable()
  delete supplementaryInsurances[key]
  return state.setIn(['supplementaryInsurances', quoteId], supplementaryInsurances)
}

const setNumberOfItemsInArrayTypeR = (state, { key, path, value }) => {
  const arrayTypes = state.arrayTypes
  const pathToData = path.split('-').filter((pathItem) => !!pathItem)
  pathToData.push(key)
  if (
    arrayTypes[key] &&
    (!arrayTypes[key].minLength || arrayTypes[key].minLength <= value) &&
    (!arrayTypes[key].maxLength || arrayTypes[key].maxLength >= value)
  ) {
    const ifAdd = arrayTypes[key].length < value

    let newArrayData = state.data.getIn(pathToData, false)
    let newState = state
    if (newArrayData) {
      newArrayData = newArrayData.asMutable()
      if (Array.isArray(newArrayData)) {
        if (ifAdd) {
          newArrayData = [...newArrayData, ...new Array(value - arrayTypes[key].length).fill({})]
        } else {
          newArrayData = newArrayData.slice(0, value)
        }
        newState = newState.setIn(['data', ...pathToData], newArrayData)
      } else {
        return newState.setIn(['arrayTypes', key, 'length'], 2).setIn(['data', ...pathToData], [{}])
      }
    }
    return newState.setIn(['arrayTypes', key, 'length'], value)
  }
  return state
}

const userDataSetValueR = (state, { key, value }) => {
  let keys = Array.isArray(key) ? key : [key]
  if (keys.includes('policyHolder') && keys.includes('phoneNumber')) {
    savePhoneNumber(value)
  }
  return state.setIn(['data', ...keys], value).setIn(['initialData'], false)
}

const userDataSetValueInsuredPartiesR = (state, { pathArray, value }) => {
  if (pathArray.includes('addPolicyHolderToMembers')) {
    let newState = state.setIn(['data', ...pathArray], value)
    if (value) {
      newState = newState.setIn(
        ['data', 'insuredParties', 'members'],
        state.data.getIn(['insuredParties', 'members'], []).flatMap((member, index) => {
          if (index === 0) {
            return {
              ...member,
              ...state.data.policyHolder,
            }
          }
          return member
        }),
      )
    }
    return newState
  }
  return state.setIn(['data', ...pathArray], value)
}

const updateArrayTypesR = (state, { specification }) => {
  const arrayTypes = updateArrayTypes(specification, state.arrayTypes.asMutable())
  return Object.keys(arrayTypes).reduce(
    (acc, arrayTypeKey) =>
      setNumberOfItemsInArrayTypeR(acc, {
        key: arrayTypeKey,
        path: arrayTypes[arrayTypeKey].path,
        value:
          arrayTypes[arrayTypeKey].length > arrayTypes[arrayTypeKey].maxLength
            ? arrayTypes[arrayTypeKey].maxLength
            : arrayTypes[arrayTypeKey].length,
      }),
    state.set('arrayTypes', arrayTypes),
  )
}

const copyDataForFieldReferencesR = (state, { stepKey, fieldReferences, quoteId }) => {
  if (Object.keys(fieldReferences).length > 0 && fieldReferences[stepKey]) {
    let data = new Immutable({
      ...state.data.asMutable(),
      supplementaryInsurances: state.supplementaryInsurances[quoteId]
        ? state.supplementaryInsurances[quoteId].asMutable()
        : {},
    })
    Object.keys(fieldReferences[stepKey]).map((pathFrom) => {
      const pathTo = fieldReferences[stepKey][pathFrom]
      const value = data.getIn(pathFrom.split('.'), false)
      if (value) {
        data = data.setIn(pathTo.split('.'), value)
      }
    })
    data = data.asMutable()
    const supplementaryInsurances = {
      ...state.supplementaryInsurances,
      [quoteId]: data.supplementaryInsurances,
    }
    delete data.supplementaryInsurances
    return state.merge({ data, supplementaryInsurances })
  } else {
    return state
  }
}

const defaultValuesRequestSuccessR = (state, { defaultValues }) => {
  if (!state.initialData || !defaultValues) {
    return state.set('defaultValues', defaultValues)
  }

  let supplementaryInsurances = state.supplementaryInsurances
  if (defaultValues.supplementaryInsurances) {
    supplementaryInsurances = supplementaryInsurances.merge(defaultValues.supplementaryInsurances)
    delete defaultValues.supplementaryInsurances
  }

  return state
    .merge({ data: state.data.merge(defaultValues, { deep: true }), supplementaryInsurances })
    .set('defaultValues', defaultValues)
}

const unsetInitialDataR = (state) => state.merge({ initialData: false })

const wasCicCompletedR = (state, { firstStepCicCompleted }) =>
  state.set('firstStepCicCompleted', firstStepCicCompleted).setIn(['data', 'cicEnabled'], firstStepCicCompleted)

const onSetRequestIdR = (state, { previousRequestId }) => state.set('previousRequestId', previousRequestId)

const setIsBackOfficeR = (state, { isBackOffice }) => state.setIn(['data', 'isBackOffice'], isBackOffice)

const onChangeDataR = (state, { data, loaded }) => {
  let newState = state
  if (data && data.policyHolder && data.policyHolder.phoneNumber) {
    savePhoneNumber(data.policyHolder.phoneNumber)
  }
  if (typeof loaded === 'object') {
    newState = newState.merge({ loadedData: loaded })
  }
  return newState.merge({ data }, { deep: true })
}

const restoreDataRequestSuccessR = (state, { data, defaultData, linkId }) => {
  let supplementaryInsurances = state.supplementaryInsurances
  const userData = data.productDescriptorValues || data.data || {}
  userData.isBackOffice = config.getConfig('isBackOffice')
  userData.cicEnabled = typeof userData.cic !== 'undefined'

  let userDataImmutable = state.data
    .merge(defaultData)
    .merge(userData, { deep: true })
    .set('defaultValues', defaultData)
  if (data.properties) {
    if (data.properties.supplementaryInsurances) {
      supplementaryInsurances = data.properties.supplementaryInsurances
    }
    if (data.properties.bonusType) {
      userDataImmutable = userDataImmutable.set('bonusType', data.properties.bonusType)
    }
    if (data.properties.monthsWithAccidents) {
      userDataImmutable = userDataImmutable.set('monthsWithAccidents', data.properties.monthsWithAccidents)
    }
    if (data.properties.countOfAccidents) {
      userDataImmutable = userDataImmutable.set('countOfAccidents', data.properties.countOfAccidents)
    }
    if (data.properties.vehicleProperties) {
      Object.keys(data.properties.vehicleProperties).forEach((key) => {
        const value = userDataImmutable.getIn([key], false)
        if (value === false || value === '') {
          userDataImmutable = userDataImmutable.set(key, data.properties.vehicleProperties[key])
        }
      })
    }
  }

  if (!userDataImmutable.type) {
    userDataImmutable = userDataImmutable.set('type', 'car')
  }
  if (typeof userData.ownerSameAsPolicyHolder === 'undefined') {
    userDataImmutable = userDataImmutable.set('ownerSameAsPolicyHolder', true)
  }
  if (typeof userData.userSameAsPolicyHolder === 'undefined') {
    userDataImmutable = userDataImmutable.set('userSameAsPolicyHolder', true)
  }
  if (typeof userData.ownerSameAsUser === 'undefined') {
    userDataImmutable = userDataImmutable.set('ownerSameAsUser', false)
  }
  if (typeof userData.policyHolder === 'undefined' || typeof userData.policyHolder.sameContactAddress === 'undefined') {
    userDataImmutable = userDataImmutable.setIn(['policyHolder', 'sameContactAddress'], true)
  }
  if (userDataImmutable.getIn(['policyHolder', 'email']) === 'info@suri.cz') {
    userDataImmutable = userDataImmutable.setIn(['policyHolder', 'email'], '')
  }
  if (userDataImmutable.getIn(['elCommunicationAgreed'])) {
    userDataImmutable = userDataImmutable.set('elCommunicationAgreed', false)
  }
  if (data.trackingParams) {
    if (!data.trackingParams.crmId) {
      data.trackingParams.crmId = ''
    }
    if (data.properties && data.properties.crmId) {
      data.trackingParams.crmId = data.properties.crmId
    }
  }

  return state.merge({
    data: INITIAL_STATE.data.merge(userDataImmutable, { deep: true }),
    initialData: false,
    trackingParams: data.trackingParams || {},
    previousRequestId: data.requestId,
    supplementaryInsurances,
    restoredLink: linkId,
  })
}

const setPaymentR = (state, { payment }) => state.setIn(['data', 'paymentPeriod'], payment)

const setInitDataSigR = (state, { initDataSig }) => state.set('initDataSig', initDataSig)

const setTrackingParamsR = (state, { trackingParams, reset }) => {
  if (reset) {
    return state.set('trackingParams', trackingParams)
  }
  return state.set('trackingParams', state.trackingParams.merge(trackingParams))
}

const setPropertiesR = (state, { properties }) => state.merge({ properties }, { deep: true })

const setSessionMinutesValueR = (state, { path, value }) => state.setIn(['sessionMinutes', ...path], value)

/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer(INITIAL_STATE, {
  [Types.COPY_DATA_FOR_FIELD_REFERENCES]: copyDataForFieldReferencesR,
  [Types.DEFAULT_VALUES_REQUEST_LOADER_OFF]: (state) => state,
  [Types.DEFAULT_VALUES_REQUEST_SUCCESS]: defaultValuesRequestSuccessR,
  [Types.ON_CHANGE_DATA]: onChangeDataR,
  [Types.ON_SET_REQUEST_ID]: onSetRequestIdR,
  [Types.RESTORE_DATA_REQUEST]: (state) => state,
  [Types.RESTORE_DATA_REQUEST_SUCCESS]: restoreDataRequestSuccessR,
  [Types.SET_IS_BACK_OFFICE]: setIsBackOfficeR,
  [Types.SET_NUMBER_OF_ITEMS_IN_ARRAY_TYPE]: setNumberOfItemsInArrayTypeR,
  [Types.SET_PAYMENT]: setPaymentR,
  [Types.SUPPLEMENTARY_INSURANCE_REMOVE]: supplementaryInsuranceRemoveR,
  [Types.SUPPLEMENTARY_INSURANCE_RESET]: supplementaryInsuranceResetR,
  [Types.SUPPLEMENTARY_INSURANCE_SET_VALUE]: supplementaryInsuranceSetValueR,
  [Types.SUPPLEMENTARY_INSURANCE_CHANGE]: supplementaryInsuranceChangeR,
  [Types.UNSET_INITIAL_DATA]: unsetInitialDataR,
  [Types.UPDATE_ARRAY_TYPES]: updateArrayTypesR,
  [Types.USER_DATA_INIT]: userDataInitR,
  [Types.USER_DATA_REMOVE_VALUE]: userDataRemoveValueR,
  [Types.USER_DATA_SET_VALUE]: userDataSetValueR,
  [Types.USER_DATA_SET_VALUE_INSURED_PARTIES]: userDataSetValueInsuredPartiesR,
  [Types.WAS_CIC_COMPLETED]: wasCicCompletedR,
  [Types.SET_INIT_DATA_SIG]: setInitDataSigR,
  [Types.SET_TRACKING_PARAMS]: setTrackingParamsR,
  [Types.SET_PROPERTIES]: setPropertiesR,
  [Types.SET_SESSION_MINUTES_VALUE]: setSessionMinutesValueR,
})
